import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, X, Check } from 'lucide-react';

const API = '/api';

export function TherapeuticAreaSelect({ value = [], onChange, testId = "therapeutic-area-select" }) {
  const [open, setOpen] = useState(false);
  const [areas, setAreas] = useState([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (open) {
      axios.get(`${API}/therapeutic-areas`, { withCredentials: true })
        .then(res => setAreas(res.data || []))
        .catch(() => {});
    }
  }, [open]);

  const handleSelect = (area) => {
    if (!value.includes(area)) {
      onChange([...value, area]);
    } else {
      onChange(value.filter(a => a !== area));
    }
  };

  const handleAddCustom = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setAreas(prev => [...new Set([...prev, trimmed])].sort((a, b) => a.localeCompare(b)));
    }
    setInputValue('');
  };

  const handleRemove = (area) => {
    onChange(value.filter(a => a !== area));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  const filtered = areas.filter(a =>
    !inputValue.trim() || a.toLowerCase().includes(inputValue.trim().toLowerCase())
  );
  const isNewEntry = inputValue.trim() && !areas.some(a => a.toLowerCase() === inputValue.trim().toLowerCase());

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {value.map(area => (
          <Badge key={area} variant="secondary" className="text-xs gap-1 pr-1">
            {area}
            <button onClick={() => handleRemove(area)} className="ml-0.5 hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid={`${testId}-trigger`}>
            <Plus className="w-3 h-3" /> Add Therapeutic Area
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <div className="p-2 border-b border-slate-100">
            <div className="flex gap-1.5">
              <Input
                placeholder="Search or type new..."
                className="h-8 text-xs"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                data-testid={`${testId}-input`}
              />
              {isNewEntry && (
                <Button
                  size="sm"
                  variant="default"
                  className="h-8 px-2.5 text-xs bg-[#00709B] hover:bg-[#005a7d] shrink-0"
                  onClick={handleAddCustom}
                  data-testid={`${testId}-add-custom`}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              )}
            </div>
          </div>
          <div className="max-h-[220px] overflow-y-auto p-1">
            {isNewEntry && (
              <button
                className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-[#00709B] font-medium hover:bg-slate-50 rounded cursor-pointer"
                onClick={handleAddCustom}
              >
                <Plus className="w-3.5 h-3.5" /> Add "{inputValue.trim()}"
              </button>
            )}
            {filtered.length === 0 && !isNewEntry && (
              <p className="text-xs text-slate-400 text-center py-3">No areas found</p>
            )}
            {filtered.map(area => {
              const selected = value.includes(area);
              return (
                <button
                  key={area}
                  className={`flex items-center gap-2 w-full px-2.5 py-1.5 text-xs rounded cursor-pointer ${selected ? 'bg-slate-100 text-slate-500' : 'hover:bg-slate-50 text-slate-700'}`}
                  onClick={() => handleSelect(area)}
                >
                  {selected && <Check className="w-3 h-3 text-emerald-500 shrink-0" />}
                  <span className={selected ? 'line-through' : ''}>{area}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
